// Embed-mode auth bootstrap (PRD §7.3).
//
// When the React app runs inside the iOS WKWebView, iOS injects
// `window.__ETHOSPHERE_TOKEN__` at document start with a Keycloak-issued
// bearer token. On silent refresh, iOS rotates the value and dispatches a
// well-known event so we can re-establish open SSE / XHR connections.
//
// This module:
//   - exposes `isEmbedded()` — synchronous predicate, safe to call before
//     any provider mounts (used by `index.js` to skip the Keycloak provider)
//   - exposes `<EmbedAuthProvider>` — keeps the axios Authorization header
//     in sync with the latest injected token by listening for
//     `ethosphere:token-refreshed`
//   - exposes `currentEmbedToken()` — re-reads `window.__ETHOSPHERE_TOKEN__`
//     at request time, so the very latest value is used (no stale cache)
//
// The token is opaque to React — backend's PolicyEnforcementMiddleware
// validates it the same way it validates browser-Keycloak tokens.

import { useEffect } from "react";
import { setToken } from "./Api";

export const TOKEN_REFRESHED_EVENT = "ethosphere:token-refreshed";

const safeWindow = () =>
  typeof window === "undefined" ? null : window;

/**
 * Read the iOS-injected token from `window.__ETHOSPHERE_TOKEN__`.
 * Returns a non-empty string, or null if absent / empty / not in a browser.
 *
 * We re-read on every call rather than caching — iOS rewrites the global
 * on refresh, and we want subsequent requests to always pick up the
 * freshest value (defense in depth on top of the refresh event).
 */
export function currentEmbedToken() {
  const w = safeWindow();
  if (!w) return null;
  const raw = w.__ETHOSPHERE_TOKEN__;
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  return trimmed.length === 0 ? null : trimmed;
}

export function isEmbedRoute() {
  const w = safeWindow();
  if (!w) return false;
  return w.location?.pathname?.startsWith("/embed/") === true;
}

export function syncEmbedToken() {
  const next = currentEmbedToken();
  setToken(next || "");
  return next;
}

/**
 * Synchronous predicate: are we running inside the iOS WebView shell?
 *
 * Used by `index.js` BEFORE any React provider mounts so we can choose
 * whether to render `<ReactKeycloakProvider>` (browser users) or skip
 * straight to `<App />` (embed users — no OAuth redirect).
 */
export function isEmbedded() {
  return isEmbedRoute() || currentEmbedToken() !== null;
}

/**
 * Mount this once near the top of the tree when `isEmbedded()` is true.
 * It seeds the Api token immediately and re-seeds on every refresh event.
 *
 * No UI — pure side-effects.
 */
export function EmbedAuthProvider({ children }) {
  useEffect(() => {
    const w = safeWindow();
    if (!w) return undefined;

    // Seed immediately — iOS already wrote the token via userScript at
    // document start, so it's available synchronously here.
    syncEmbedToken();

    w.addEventListener(TOKEN_REFRESHED_EVENT, syncEmbedToken);
    return () => {
      w.removeEventListener(TOKEN_REFRESHED_EVENT, syncEmbedToken);
    };
  }, []);

  return children;
}

export default EmbedAuthProvider;
